import { useCallback, useEffect, useState } from 'react'
import { createTranslator, type Lang } from '@romain130492/topchopsticks'

/**
 * Language, shared app-wide.
 *
 * Defaults to Chinese: the audience is in Shanghai and the mini program does
 * the same. Remembered per browser; the header toggle flips it.
 */
const KEY = 'tc_lang'
const listeners = new Set<(lang: Lang) => void>()

const read = (): Lang => {
  try {
    const stored = localStorage.getItem(KEY)
    if (stored === 'en' || stored === 'zh') return stored
  } catch { /* private mode */ }
  return navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'zh'
}

let current: Lang = read()

export function setLang(next: Lang) {
  current = next
  try { localStorage.setItem(KEY, next) } catch { /* noop */ }
  document.documentElement.lang = next === 'zh' ? 'zh-CN' : 'en'
  listeners.forEach((listener) => listener(next))
}

export function useI18n() {
  const [lang, setLocal] = useState<Lang>(current)

  useEffect(() => {
    listeners.add(setLocal)
    return () => { listeners.delete(setLocal) }
  }, [])

  const toggle = useCallback(() => setLang(lang === 'zh' ? 'en' : 'zh'), [lang])
  const { t, L } = createTranslator(lang)
  return { t, L, lang, toggle }
}
